import { useCallback, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import {
  AsYouType,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'libphonenumber-js'
import {
  PiCaretDown,
  PiChecks,
  PiCopy,
  PiEnvelope,
  PiForkKnife,
  PiGear,
  PiGlobe,
  PiLinkedinLogo,
  PiMapPin,
  PiNote,
  PiPaperclip,
  PiPaperPlaneRight,
  PiPencilSimple,
  PiPhone,
  PiPulse,
  PiPushPin,
  PiPushPinSlash,
  PiTray,
  PiTrash,
  PiWarning,
  PiWarningCircle,
} from 'react-icons/pi'
import {
  ActivityComposer,
  ActivityTimeline,
  type ActivityItem,
  AddPill,
  Alert,
  AlertContent,
  AlertDescription,
  AlertIndicator,
  AlertTitle,
  Backdrop,
  Badge,
  type BadgeColor,
  Button,
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  DetailHeader,
  DetailPanel,
  DetailSection,
  Dialog,
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EditableInfoRow,
  type EditableSelectOption,
  EntityDetailRail,
  EntityRow,
  EntitySummaryCard,
  EntitySummaryRow,
  FilterChip,
  InfoList,
  InfoRow,
  Input,
  MultiCombobox,
  type MultiComboboxOption,
  OverlayWrapper,
  PeriodPicker,
  type PeriodQuickSelect,
  type PeriodValue,
  type RichTextCommand,
  RichTextEditor,
  SearchBar,
  Skeleton,
  StatusStat,
  TabContent,
  TabList,
  TabRoot,
  TabTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@firstbite/ui'
import useApiService from 'services/api'
import {
  DepartmentLabels,
  EmailValidity,
  EmailValidityLabels,
  SeniorityLabels,
  ContactSourceLabels,
  contactHasEmail,
  type ContactCreateRequest,
  type ContactDoor,
  type ContactResponseDetail,
} from 'models/contacts'
import type { Tag } from 'models/tags'
import type { INote } from 'models/contact_companies'
import { useCompanyUrl } from 'utils/useCompanyUrl'
import { getApiErrorMessage } from 'utils/getApiErrorDetail'
import { usePinTarget } from 'components/QuickAccess/usePinTarget'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import EditContactModal from 'components/Modals/EditContactModal'
import { TimelineEmail } from 'components/EntityDetailPage/TimelineEmail'
import { DeleteIconButton } from 'components/EntityDetailPage/DeleteIconButton'
import { freshnessColorFromDate } from 'components/EntityDetailPage/freshness'
import { useNoteMentions } from 'components/EntityDetailPage/useNoteMentions'
import { useNoteEditAttachments } from 'components/EntityDetailPage/useNoteEditAttachments'
import { TagPill } from 'components/EntityDetailPage/TagPill'
import { useConfirmDialog } from 'components/EntityDetailPage/useConfirmDialog'
import {
  RICH_TEXT_EXEC,
  activeRichTextCommands,
  escapeHtml,
} from 'utils/richTextComposer'

// ─── constants / helpers ──────────────────────────────────────────────────────

/** Plain-text preview of a note's rich-text (HTML) content. */
function stripHtml(html: string): string {
  const el = document.createElement('div')
  el.innerHTML = html
  return (el.textContent ?? '').trim()
}

/** Enum → {value,label} option lists for the seniority / department selects. */
const SENIORITY_SELECT: EditableSelectOption[] = (
  Object.entries(SeniorityLabels) as [string, string][]
).map(([value, label]) => ({ value, label }))
const DEPARTMENT_SELECT: EditableSelectOption[] = (
  Object.entries(DepartmentLabels) as [string, string][]
).map(([value, label]) => ({ value, label }))

/** A single-field PATCH of the contact record. */
type ContactPatch = Partial<ContactCreateRequest>

/**
 * Bridges the note toolbar's `onCommand` to the browser's `execCommand` so the
 * formatting buttons act on the `contentEditable` surface. `execCommand` is
 * deprecated but dependency-free and is exactly what fb-ui's RichTextEditor
 * story uses as the reference wiring. `link` prompts for a URL; `attachment`
 * is handled separately (opens the hidden file picker). The composed HTML is
 * persisted via the contact-notes endpoint on save.
 */
/** Map an email-validity enum to a Badge color (AA pairings baked into fb-ui). */
function emailValidityColor(validity: number | null | undefined): BadgeColor {
  switch (validity) {
    case EmailValidity.VALID:
      return 'success'
    case EmailValidity.INVALID:
    case EmailValidity.SPAMTRAP:
    case EmailValidity.ABUSE:
    case EmailValidity.DO_NOT_MAIL:
      return 'error'
    case EmailValidity.CATCH_ALL:
    case EmailValidity.UNKNOWN:
      return 'amber'
    default:
      return 'gray'
  }
}

/** Title-case a workflow-status slug (e.g. "in_progress" → "In Progress"). */
function campaignStatusLabel(s?: string | null): string {
  if (!s) return ''
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function fmtDate(value?: string | null): string {
  if (!value) return '—'
  return dayjs(value).format('MMM D, YYYY')
}

function fmtTimestamp(value?: number | null): string {
  if (!value) return '—'
  return dayjs(value).format('MMM D, YYYY')
}

// ─── Phone helpers (US) ─────────────────────────────────────────────────────

/** Mask a phone draft as the user types: digits only, US national format
 *  (drops a leading US country code, caps at 10 digits). */
function formatPhoneDraft(raw: string): string {
  let digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  return new AsYouType('US').input(digits)
}

/** Canonicalize a valid phone to E.164 for storage (falls back to the raw
 *  string if it can't be parsed — validation should prevent that on save). */
function toE164(value: string): string {
  try {
    return parsePhoneNumber(value, 'US')?.number ?? value
  } catch {
    return value
  }
}

/** Nicely format a stored phone for display; returns the raw value for
 *  unparseable legacy data so nothing crashes. */
function displayPhone(value?: string | null): string | undefined {
  if (!value) return undefined
  try {
    return parsePhoneNumber(value, 'US')?.formatNational() ?? value
  } catch {
    return value
  }
}

/** Prepend `https://` to a bare-domain website (e.g. `example.com`) so the
 *  backend `URLField` — which requires a scheme — accepts it. Already-schemed
 *  and empty values pass through unchanged. */
function normalizeWebsite(value: string): string {
  const v = value.trim()
  if (!v) return ''
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`
}

/** A `tel:` URI for a phone number, auto-dialing the extension after the call
 *  connects. An extension is only ever entered *after* the destination's phone
 *  system answers, so we append it as comma-pause DTMF (`,,` ≈ two ~2s pauses,
 *  then the digits) — the dialer places the call, waits for the auto-attendant,
 *  then sends the extension for the rep. (RFC 3966's `;ext=` only *labels* the
 *  extension; most dialers don't send it, leaving the manual punch-in.) The
 *  fixed pause is a blind timer, so a slow attendant or a human answering can
 *  mistime the tones — an acceptable trade for one-tap dialing in the CRM. */
function telHref(phone: string, extension?: string | null): string {
  const ext = extension?.trim()
  return `tel:${phone}${ext ? `,,${ext}` : ''}`
}

// ─── Contact Detail Page ──────────────────────────────────────────────────────

/** Top-level page for viewing and editing a single contact — mirrors the Deal
 *  detail page in spirit, assembled from @firstbite/ui detail-page blocks.
 *  Panel fields edit inline (PATCH `contacts/update/:id`); labels open an fb-ui
 *  Dialog wired to the contact-tags bulk endpoint. */
export function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>()
  const navigate = useNavigate()
  const getCompanyUrl = useCompanyUrl()
  const api = useApiService()
  const queryClient = useQueryClient()
  const {
    enableQuickAccess,
    enableContactDetailPage,
    isLoading: flagsLoading,
  } = useFeatureFlagService()

  const contactIdNum = contactId ? parseInt(contactId, 10) : undefined
  const {
    data: contact,
    isLoading,
    isError,
  } = api.useGetContactDetails(contactIdNum ?? 0, undefined, !!contactIdNum)

  const [labelModalOpen, setLabelModalOpen] = useState(false)
  const [editContactOpen, setEditContactOpen] = useState(false)
  const [editCompanyOpen, setEditCompanyOpen] = useState(false)
  const { isPinned, toggle: togglePin } = usePinTarget({
    targetType: 'contact',
    targetId: contactIdNum ?? 0,
  })

  /** Refresh the cached contact detail after any mutation. */
  const refreshContact = useCallback(() => {
    if (contactIdNum) {
      void queryClient.invalidateQueries({
        queryKey: ['contact', contactIdNum],
      })
    }
  }, [queryClient, contactIdNum])

  /** PATCH a single field (or few) of the contact, then refetch + toast. */
  const saveField = useCallback(
    async (patch: ContactPatch) => {
      if (!contactIdNum) return
      try {
        await api.updateContact(contactIdNum, patch as ContactCreateRequest)
        refreshContact()
        toast.success('Contact updated')
      } catch (err) {
        // Surface the backend's field message (e.g. "Enter a valid URL.")
        // instead of a generic failure, so the user knows what to fix.
        toast.error(getApiErrorMessage(err, 'Failed to update contact'))
      }
    },
    [api, contactIdNum, refreshContact]
  )

  const { confirm, dialog: deleteContactDialog } = useConfirmDialog()
  const handleDelete = useCallback(async () => {
    if (!contactIdNum) return
    const ok = await confirm({
      title: 'Delete this contact?',
      description: 'This permanently removes the contact and cannot be undone.',
      confirmLabel: 'Delete contact',
    })
    if (!ok) return
    try {
      await api.deleteContact(contactIdNum)
      toast.success('Contact deleted')
      navigate('/contacts')
    } catch {
      toast.error('Failed to delete contact')
    }
  }, [api, contactIdNum, navigate, confirm])

  // Real contact notes (content + attachments) from the backend. Email/created
  // events below are still derived from the contact's timestamps (no activity
  // endpoint for those yet).
  const { data: notesData, refetch: refetchNotes } = useQuery({
    queryKey: ['contactNotes', contactIdNum],
    queryFn: () => api.getContactNotes(contactIdNum ?? 0),
    enabled: !!contactIdNum,
    retry: false,
  })

  const noteItems = useMemo<ActivityItem[]>(
    () =>
      (notesData?.results ?? []).map((note) => ({
        id: `note-${note.id}`,
        timestamp: note.created,
        type: 'note',
        actor: note.user
          ? `${note.user.first_name} ${note.user.last_name}`.trim() || 'You'
          : 'You',
        summary: (
          <>
            added a <strong>note</strong>
          </>
        ),
        detail: stripHtml(note.content),
        expandedContent:
          contactIdNum != null ? (
            <ContactNoteView
              contactId={contactIdNum}
              note={note}
              onChanged={refetchNotes}
            />
          ) : undefined,
      })),
    [notesData, contactIdNum, refetchNotes]
  )

  const derivedActivity = useMemo<ActivityItem[]>(() => {
    if (!contact) return []
    const items: ActivityItem[] = []
    const who = `${contact.first_name} ${contact.last_name}`.trim() || 'Contact'

    if (contact.date_last_received) {
      const body = `Thanks for reaching out — this looks interesting. Could you send over pricing and a sample so we can trial it?`
      // Mock inbound attachment — demonstrates surfacing attachments on an
      // email activity (a paperclip on the row + a download chip on expand).
      // The chip is a link whenever `url` is set, so a real inbound attachment
      // opens in a new tab; this mock uses a data URL so it's clickable too.
      const attachments = [
        {
          name: 'current-supplier-invoice.pdf',
          url: 'data:text/plain;charset=utf-8,Mock%20attachment%20%E2%80%94%20a%20real%20inbound%20email%20would%20link%20to%20the%20actual%20file.',
        },
      ]
      items.push({
        id: 'evt-received',
        timestamp: contact.date_last_received,
        type: 'email',
        color: 'success',
        actor: who,
        summary: (
          <>
            replied to an <strong>email</strong>
            {attachments.length > 0 ? (
              <span
                className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-fg-tertiary"
                title={`${attachments.length} attachment${attachments.length > 1 ? 's' : ''}`}
              >
                <PiPaperclip size={12} aria-hidden="true" />
                {attachments.length}
              </span>
            ) : null}
          </>
        ),
        detail: body,
        expandedContent: (
          <TimelineEmail
            from={who}
            subject="Re: First Bite intro"
            body={body}
            attachments={attachments}
          />
        ),
      })
    }
    if (contact.date_last_sent) {
      const body = `Hi ${contact.first_name || 'there'} — following up on my note. Happy to set up a quick call to walk through the details whenever works for you.`
      items.push({
        id: 'evt-sent',
        timestamp: contact.date_last_sent,
        type: 'email',
        actor: 'You',
        summary: (
          <>
            sent an <strong>email</strong>
          </>
        ),
        detail: body,
        expandedContent: (
          <TimelineEmail from="You" subject="First Bite intro" body={body} />
        ),
      })
    }
    if (contact.seq_membership_status) {
      items.push({
        id: 'evt-seq',
        timestamp: contact.date_last_sent ?? contact.created,
        type: 'campaign',
        actor: 'First Bite',
        summary: (
          <>
            enrolled in a <strong>sequence</strong>
            {contact.seq_total_steps ? (
              <>
                {' '}
                · step {contact.seq_steps_completed ?? 0} of{' '}
                {contact.seq_total_steps}
              </>
            ) : null}
          </>
        ),
      })
    }
    if (contact.created) {
      items.push({
        id: 'evt-created',
        timestamp: contact.created,
        type: 'contact',
        actor:
          contact.source != null
            ? ContactSourceLabels[contact.source as 0 | 1]
            : 'First Bite',
        summary: (
          <>
            added this <strong>contact</strong>
          </>
        ),
      })
    }
    return items
  }, [contact])

  // Activity filters — a text search + a period (quick-select or custom range),
  // applied client-side to the feed.
  const [activitySearch, setActivitySearch] = useState('')
  const [activityPeriod, setActivityPeriod] = useState<PeriodValue | null>(null)
  const activityQuickSelects = useMemo<PeriodQuickSelect[]>(() => {
    const now = new Date()
    const daysAgo = (d: number) => {
      const x = new Date(now)
      x.setDate(x.getDate() - d)
      return x
    }
    const range = (label: string, days: number): PeriodQuickSelect => ({
      label,
      value: { mode: 'custom', start: daysAgo(days), end: now, label },
    })
    // "All time" is intentionally omitted — clearing the filter is handled by
    // the trigger's × and the footer Clear button instead.
    return [
      range('Last week', 7),
      range('Last month', 30),
      range('Last 3 months', 90),
      range('Last 6 months', 180),
      range('Last year', 365),
    ]
  }, [])

  const allActivity = useMemo(() => {
    const merged = [...noteItems, ...derivedActivity]
    const q = activitySearch.trim().toLowerCase()
    const start = activityPeriod?.start ?? null
    const end = activityPeriod?.end ?? null
    if (!q && !start && !end) return merged
    return merged.filter((item) => {
      if (q) {
        const hay = `${typeof item.detail === 'string' ? item.detail : ''} ${
          typeof item.actor === 'string' ? item.actor : ''
        }`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      if (start || end) {
        const t = new Date(item.timestamp as string | number | Date).getTime()
        if (start && t < start.getTime()) return false
        if (end && t > end.getTime()) return false
      }
      return true
    })
  }, [noteItems, derivedActivity, activitySearch, activityPeriod])

  const noteActivity = useMemo(
    () => allActivity.filter((i) => i.type === 'note'),
    [allActivity]
  )
  const emailActivity = useMemo(
    () => allActivity.filter((i) => i.type === 'email'),
    [allActivity]
  )

  // The composer's contentEditable surface. Toolbar commands act on it, and
  // Save reads its text into the logged item. `noteEmpty` gates the footer so
  // Discard / Save only appear once something's been typed.
  const noteMentions = useNoteMentions()
  const noteRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteEmpty, setNoteEmpty] = useState(true)
  const [activeCommands, setActiveCommands] = useState<RichTextCommand[]>([])
  const syncNoteEmpty = () =>
    setNoteEmpty((noteRef.current?.textContent ?? '').trim() === '')

  // Reflect the toolbar's pressed state from the current selection, so an
  // active format (bold, a list, blockquote/code) stays highlighted until
  // it's toggled off — not just while hovered.
  const refreshActiveCommands = () => {
    setActiveCommands(activeRichTextCommands())
  }

  // Insert-link dialog (an app dialog rather than the native prompt). The
  // editor selection is captured when the command fires and restored when the
  // link is inserted, since opening the dialog moves focus off the editor.
  const savedRange = useRef<Range | null>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  const handleCommand = (cmd: RichTextCommand) => {
    if (cmd === 'link') {
      const sel = window.getSelection()
      savedRange.current =
        sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null
      setLinkUrl('')
      setLinkOpen(true)
      return
    }
    if (cmd === 'attachment') {
      fileInputRef.current?.click()
      return
    }
    noteRef.current?.focus()
    RICH_TEXT_EXEC[cmd]?.()
    refreshActiveCommands()
  }

  const insertLink = () => {
    const raw = linkUrl.trim()
    setLinkOpen(false)
    if (!raw) return
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
    // Defer past the dialog's unmount so the editor can take focus back.
    requestAnimationFrame(() => {
      noteRef.current?.focus()
      const sel = window.getSelection()
      if (savedRange.current && sel) {
        sel.removeAllRanges()
        sel.addRange(savedRange.current)
      }
      if (sel && sel.isCollapsed) {
        document.execCommand(
          'insertHTML',
          false,
          `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`
        )
      } else {
        document.execCommand('createLink', false, url)
      }
      syncNoteEmpty()
    })
  }

  const clearComposer = () => {
    if (noteRef.current) noteRef.current.innerHTML = ''
    setAttachedFiles([])
    setNoteEmpty(true)
    setActiveCommands([])
  }

  const handleLog = async () => {
    if (!contactIdNum) return
    const html = noteRef.current?.innerHTML ?? ''
    const text = noteRef.current?.textContent?.trim() ?? ''
    if (!text && attachedFiles.length === 0) return
    setNoteSaving(true)
    try {
      await api.createContactNote(contactIdNum, html, attachedFiles)
      clearComposer()
      await refetchNotes()
      toast.success('Note saved')
    } catch {
      toast.error('Failed to save note')
    } finally {
      setNoteSaving(false)
    }
  }

  const handleDiscard = () => {
    clearComposer()
  }

  // Feature-flagged (`enable_contact_detail_page`, off by default) so the page
  // can merge + release dark until we flip the flag. Direct-URL hits with the
  // flag off bounce back to the contacts table. Wait for flags to resolve first
  // so a flag-on user isn't redirected during the preferences load.
  if (!flagsLoading && !enableContactDetailPage) {
    return <Navigate to="/contacts" replace />
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-surface-sunken">
        <Skeleton className="h-6 w-40" />
      </div>
    )
  }

  if (!contact || isError || !contactIdNum) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-surface-sunken">
        <p className="text-fg-secondary">Contact not found.</p>
        <Button
          size="sm"
          appearance="outline"
          variant="neutral"
          onClick={() => navigate('/contacts')}
        >
          Back to Contacts
        </Button>
      </div>
    )
  }

  const contactName =
    `${contact.first_name} ${contact.last_name}`.trim() || 'Contact'

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface-sunken">
      {/* Attached-rail layout: the summary/metadata panel is a flush, full-height
          left rail (reference chrome); the header + activity live in the scrolling
          work area. Back nav lives in the top-nav breadcrumb. */}
      <div className="min-h-0 flex-1">
        <EntityDetailRail
          rail={
            <ContactSidebar
              contact={contact}
              getCompanyUrl={getCompanyUrl}
              saveField={saveField}
              onAddLabel={() => setLabelModalOpen(true)}
              onEditCompany={() => setEditCompanyOpen(true)}
              onOpenDeal={(dealId) => navigate(`/deals/${dealId}`)}
            />
          }
        >
          {/* Do-not-contact is a hard constraint, not a stat — a persistent,
              non-dismissable danger banner above the header. */}
          {contact.is_do_not_contact ? (
            <Alert status="error" className="mb-4">
              <AlertIndicator />
              <AlertContent>
                <AlertTitle>Do not contact</AlertTitle>
                <AlertDescription>
                  This contact is marked do-not-contact. Don’t email, call, or
                  add them to a campaign.
                </AlertDescription>
              </AlertContent>
            </Alert>
          ) : null}

          <DetailHeader
            eyebrow="Contact"
            title={contactName}
            stats={<ContactHeaderStats contact={contact} />}
            actions={
              <>
                {/* Channel icons live beside Actions (not next to the name) so
                    the title stays clean above the stats row. */}
                <ContactHeaderChannels contact={contact} />
                <ContactHeaderActions
                  contact={contact}
                  isPinned={isPinned}
                  onTogglePin={togglePin}
                  onDelete={handleDelete}
                  onEditContact={() => setEditContactOpen(true)}
                  showPin={enableQuickAccess}
                />
              </>
            }
          />

          {/* Note composer */}
          <div className="mt-2">
            <ActivityComposer
              types={['note']}
              contentRef={noteRef}
              mentions={noteMentions}
              discardLabel="Cancel"
              disabled={noteSaving}
              onCommand={handleCommand}
              activeCommands={activeCommands}
              onInput={() => {
                syncNoteEmpty()
                refreshActiveCommands()
              }}
              onKeyUp={refreshActiveCommands}
              onMouseUp={refreshActiveCommands}
              // Footer (Save / Cancel) shows once there's text or an attachment.
              onLog={
                noteEmpty && attachedFiles.length === 0 ? undefined : handleLog
              }
              onDiscard={
                noteEmpty && attachedFiles.length === 0
                  ? undefined
                  : handleDiscard
              }
              // Attachment chips sit left-aligned in the footer, opposite the
              // Save / Cancel actions. Only passed when files exist so an empty
              // note doesn't render a chrome-only footer.
              footerStart={
                attachedFiles.length > 0
                  ? attachedFiles.map((file, i) => (
                      <FilterChip
                        key={`${file.name}-${i}`}
                        value={
                          <span className="inline-flex items-center gap-1">
                            <PiPaperclip size={12} />
                            <span className="max-w-[12rem] truncate">
                              {file.name}
                            </span>
                          </span>
                        }
                        removeLabel={`Remove ${file.name}`}
                        onRemove={() =>
                          setAttachedFiles((prev) =>
                            prev.filter((_, idx) => idx !== i)
                          )
                        }
                      />
                    ))
                  : undefined
              }
            />
            {/* Hidden picker wired to the composer's paperclip command. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              aria-label="Attach files to note"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? [])
                if (files.length)
                  setAttachedFiles((prev) => [...prev, ...files])
                e.target.value = ''
              }}
            />
          </div>

          {/* Activity feed */}
          <h2 className="sr-only">Activity</h2>
          <TabRoot defaultValue="all" variant="minimal" className="mt-6">
            <TabList aria-label="Contact activity">
              <TabTrigger value="all">All Activity</TabTrigger>
              <TabTrigger
                value="notes"
                badge={noteActivity.length || undefined}
              >
                Notes
              </TabTrigger>
              <TabTrigger
                value="emails"
                badge={emailActivity.length || undefined}
              >
                Emails
              </TabTrigger>
            </TabList>

            {/* Search + period filter — side by side, just below the tabs.
                No extra top margin: the tab row already leaves ~13px, enough for
                the Period label to breathe without feeling spacious for a CRM. */}
            <div className="grid grid-cols-2 gap-2">
              <SearchBar
                size="md"
                shape="rect"
                placeholder="Search activity by keyword"
                aria-label="Search activity by keyword"
                clearable
                value={activitySearch}
                onValueChange={setActivitySearch}
                onClear={() => setActivitySearch('')}
              />
              <PeriodPicker
                label="Period"
                size="md"
                shape="rect"
                showModes={false}
                quickSelects={activityQuickSelects}
                neutralDefault={{
                  mode: 'custom',
                  start: null,
                  end: null,
                  label: 'All time',
                }}
                onApply={setActivityPeriod}
                onClear={() => setActivityPeriod(null)}
              />
            </div>

            <TabContent value="all" className="pt-2">
              <ActivityTimeline
                items={allActivity}
                size="md"
                emptyState={
                  <ActivityEmpty
                    label="No activity yet"
                    icon={<PiPulse size={28} />}
                  />
                }
              />
            </TabContent>
            <TabContent value="notes" className="pt-2">
              <ActivityTimeline
                items={noteActivity}
                size="md"
                emptyState={
                  <ActivityEmpty
                    label="No notes yet — log one above."
                    icon={<PiNote size={28} />}
                  />
                }
              />
            </TabContent>
            <TabContent value="emails" className="pt-2">
              <ActivityTimeline
                items={emailActivity}
                size="md"
                emptyState={
                  <ActivityEmpty
                    label="No emails yet."
                    icon={<PiEnvelope size={28} />}
                  />
                }
              />
            </TabContent>
          </TabRoot>
        </EntityDetailRail>
      </div>

      <AddLabelModal
        open={labelModalOpen}
        onOpenChange={setLabelModalOpen}
        contactId={contactIdNum}
        currentTags={contact.tags ?? []}
        onSaved={refreshContact}
      />

      <EditCompanyModal
        open={editCompanyOpen}
        onOpenChange={setEditCompanyOpen}
        contactId={contactIdNum}
        currentCompanyName={contact.contact_company?.name}
        onSaved={refreshContact}
      />

      {editContactOpen ? (
        <EditContactModal
          show={editContactOpen}
          handleClose={() => setEditContactOpen(false)}
          contact={contact}
          onContactUpdated={refreshContact}
        />
      ) : null}

      {deleteContactDialog}

      <OverlayWrapper isOpen={linkOpen} onOpenChange={setLinkOpen}>
        <Backdrop className="max-w-sm">
          <Dialog>
            <DialogHeader>
              <DialogTitle>Insert link</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Input
                // eslint-disable-next-line jsx-a11y/no-autofocus -- expected focus on dialog open
                autoFocus
                type="url"
                placeholder="https://example.com"
                aria-label="Link URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    insertLink()
                  }
                }}
              />
            </DialogBody>
            <DialogFooter>
              <DialogClose appearance="outline">Cancel</DialogClose>
              <Button onClick={insertLink}>Insert link</Button>
            </DialogFooter>
          </Dialog>
        </Backdrop>
      </OverlayWrapper>
    </div>
  )
}

// ─── Header slots ─────────────────────────────────────────────────────────────

/** Header freshness/consent stats — DNC warning, plus last received/sent. */
/** Quick-action icon links beside the name — email / call / LinkedIn (only the
 *  channels present). 16px, gray at rest, brand-green on hover. */
function ContactHeaderChannels({
  contact,
}: {
  contact: ContactResponseDetail
}) {
  const email =
    contact.email_preferred ||
    contact.email_work ||
    contact.email_personal ||
    contact.email_generic
  const links = [
    email && {
      key: 'email',
      icon: <PiEnvelope size={16} />,
      label: 'Send email',
      href: `mailto:${email}`,
    },
    contact.phone_number && {
      key: 'phone',
      icon: <PiPhone size={16} />,
      label: 'Call',
      href: telHref(contact.phone_number, contact.extension),
    },
    contact.linkedin_url && {
      key: 'linkedin',
      icon: <PiLinkedinLogo size={16} />,
      label: 'View LinkedIn',
      href: contact.linkedin_url,
      external: true,
    },
  ].filter(Boolean) as {
    key: string
    icon: React.ReactNode
    label: string
    href: string
    external?: boolean
  }[]

  if (!links.length) return null
  return (
    <span className="flex items-center gap-1">
      {links.map((l) => (
        <a
          key={l.key}
          href={l.href}
          aria-label={l.label}
          title={l.label}
          {...(l.external ? { target: '_blank', rel: 'noreferrer' } : {})}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-tertiary transition-colors hover:bg-surface-hover hover:text-brand-active"
        >
          {l.icon}
        </a>
      ))}
    </span>
  )
}

function ContactHeaderStats({ contact }: { contact: ContactResponseDetail }) {
  // Do-not-contact is surfaced as a persistent danger banner above the header,
  // not a stat here.
  return (
    <>
      {contact.date_last_received ? (
        // Staleness-colored (green → amber → red by how recent), matching the
        // deal header — how long since you last heard from / reached the contact.
        <StatusStat
          layout="inline"
          iconChip
          size="md"
          color={freshnessColorFromDate(contact.date_last_received) ?? 'gray'}
          icon={<PiTray />}
        >
          Received {fmtTimestamp(contact.date_last_received)}
        </StatusStat>
      ) : null}
      {contact.date_last_sent ? (
        <StatusStat
          layout="inline"
          iconChip
          size="md"
          color={freshnessColorFromDate(contact.date_last_sent) ?? 'gray'}
          icon={<PiPaperPlaneRight />}
        >
          Sent {fmtTimestamp(contact.date_last_sent)}
        </StatusStat>
      ) : null}
    </>
  )
}

/** Header controls — an Inbox button + an Actions menu, mirroring the deal
 *  detail page. */
function ContactHeaderActions({
  contact,
  isPinned,
  onTogglePin,
  onDelete,
  onEditContact,
  showPin,
}: {
  contact: ContactResponseDetail
  isPinned: boolean
  onTogglePin: () => void
  onDelete: () => void
  onEditContact: () => void
  showPin: boolean
}) {
  const primaryEmail =
    contact.email_preferred ||
    contact.email_work ||
    contact.email_personal ||
    contact.email_generic
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Arbitrary `ml-[3px]` (not `ml-1`) buys a little breathing room from
            the channel icons without Bootstrap's spacing utilities shadowing it. */}
        <Button size="md" className="ml-[3px]">
          Actions
          <PiCaretDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem icon={<PiPencilSimple />} onSelect={onEditContact}>
          Edit contact
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {contact.phone_number ? (
          <DropdownMenuItem
            icon={<PiPhone />}
            onSelect={() => {
              window.location.href = telHref(
                contact.phone_number,
                contact.extension
              )
            }}
          >
            Call
          </DropdownMenuItem>
        ) : null}
        {contactHasEmail(contact) && primaryEmail ? (
          <DropdownMenuItem
            icon={<PiEnvelope />}
            onSelect={() => {
              window.location.href = `mailto:${primaryEmail}`
            }}
          >
            Send email
          </DropdownMenuItem>
        ) : null}
        {contact.linkedin_url ? (
          <DropdownMenuItem
            icon={<PiLinkedinLogo />}
            onSelect={() =>
              window.open(contact.linkedin_url, '_blank', 'noreferrer')
            }
          >
            View LinkedIn
          </DropdownMenuItem>
        ) : null}
        {showPin ? (
          <DropdownMenuItem
            icon={isPinned ? <PiPushPinSlash /> : <PiPushPin />}
            onSelect={() => {
              onTogglePin()
              toast.success(
                isPinned ? 'Unpinned from homepage' : 'Pinned to homepage'
              )
            }}
          >
            {isPinned ? 'Unpin from homepage' : 'Pin to homepage'}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive icon={<PiTrash />} onSelect={onDelete}>
          Delete contact
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

/** How many location rows to show before collapsing into a "+N more" row. */
const MAX_VISIBLE_DOORS = 4

/** A door's one-line address for the Company section — street, city, state when
 *  present, falling back to the door name (often the chain) or its id. */
function formatDoorLine(door: ContactDoor): string {
  const address = [door.address, door.city, door.state]
    .filter(Boolean)
    .join(', ')
  return address || door.name || `Location ${door.id}`
}

/** The left DetailPanel — Summary, Company, Contact Methods, Engagement,
 *  Deals, and Labels sections. Editable fields PATCH via `saveField`. */
function ContactSidebar({
  contact,
  getCompanyUrl,
  saveField,
  onAddLabel,
  onEditCompany,
  onOpenDeal,
}: {
  contact: ContactResponseDetail
  getCompanyUrl: ReturnType<typeof useCompanyUrl>
  saveField: (patch: ContactPatch) => void
  onAddLabel: () => void
  onEditCompany: () => void
  onOpenDeal: (dealId: number) => void
}) {
  const company = contact.contact_company
  const companyUrl = company ? getCompanyUrl(company) : '-'
  // Captured as a const (not read off `contact` inside the copy handler) so its
  // truthiness narrows into that closure — a property access wouldn't.
  const phoneExt = contact.extension?.trim()
  // The deals THIS contact is linked to (ContactDeal M2M), not the company's
  // whole pipeline — see the contact detail serializer's `deals` field.
  const deals = contact.deals ?? []
  const hasSequence = contact.seq_membership_status != null

  return (
    <DetailPanel variant="rail">
      <DetailSection title="Summary">
        <InfoList>
          <EditableInfoRow
            label="First Name"
            value={contact.first_name}
            placeholder="Add first name"
            onCommit={(v) => saveField({ first_name: v })}
          />
          <EditableInfoRow
            label="Last Name"
            value={contact.last_name}
            placeholder="Add last name"
            onCommit={(v) => saveField({ last_name: v })}
          />
          <EditableInfoRow
            label="Title"
            value={contact.title}
            placeholder="Add title"
            onCommit={(v) => saveField({ title: v })}
          />
          <EditableInfoRow
            label="Seniority"
            value={contact.seniority != null ? String(contact.seniority) : ''}
            displayValue={
              contact.seniority
                ? SeniorityLabels[
                    contact.seniority as keyof typeof SeniorityLabels
                  ]
                : undefined
            }
            options={SENIORITY_SELECT}
            placeholder="Set seniority"
            onCommit={(v) => saveField({ seniority: v ? Number(v) : null })}
          />
          <EditableInfoRow
            label="Department"
            value={contact.department != null ? String(contact.department) : ''}
            displayValue={
              contact.department
                ? DepartmentLabels[
                    contact.department as keyof typeof DepartmentLabels
                  ]
                : undefined
            }
            options={DEPARTMENT_SELECT}
            placeholder="Set department"
            onCommit={(v) => saveField({ department: v ? Number(v) : null })}
          />
          <InfoRow label="Source">
            {contact.source != null
              ? ContactSourceLabels[contact.source as 0 | 1]
              : '—'}
          </InfoRow>
          <InfoRow label="Date Added" numeric>
            {fmtDate(contact.created)}
          </InfoRow>
        </InfoList>
      </DetailSection>

      {company ? (
        <DetailSection
          title="Company"
          revealActions
          headerRight={
            <Button
              variant="neutral"
              iconOnly
              size="sm"
              aria-label="Edit company"
              onClick={onEditCompany}
            >
              <PiPencilSimple />
            </Button>
          }
        >
          <EntitySummaryCard
            bordered={false}
            type="company"
            name={company.name}
            href={companyUrl !== '-' ? companyUrl : undefined}
            subtitle={
              company.sub_category?.name ?? company.category ?? 'Company'
            }
          >
            {company.size ? (
              <EntitySummaryRow icon={<PiForkKnife />}>
                {company.size.toLocaleString()} locations
                {contact.company_cuisine_50
                  ? ` · ${contact.company_cuisine_50}`
                  : ''}
              </EntitySummaryRow>
            ) : contact.company_cuisine_50 ? (
              <EntitySummaryRow icon={<PiForkKnife />}>
                {contact.company_cuisine_50}
              </EntitySummaryRow>
            ) : null}
            {/* When the contact is scoped to specific locations (not the whole
                chain), list those doors' addresses — the chain card alone can't
                convey which locations this person actually covers. */}
            {contact.apply_to === 'select_locations' &&
            contact.doors?.length ? (
              <>
                <EntitySummaryRow icon={<PiMapPin />}>
                  {contact.doors.length === 1
                    ? '1 location'
                    : `${contact.doors.length} locations`}
                </EntitySummaryRow>
                {contact.doors.slice(0, MAX_VISIBLE_DOORS).map((door) => (
                  <EntitySummaryRow key={door.id} className="pl-6">
                    {formatDoorLine(door)}
                  </EntitySummaryRow>
                ))}
                {contact.doors.length > MAX_VISIBLE_DOORS ? (
                  <EntitySummaryRow className="pl-6">
                    +{contact.doors.length - MAX_VISIBLE_DOORS} more
                  </EntitySummaryRow>
                ) : null}
              </>
            ) : null}
          </EntitySummaryCard>
        </DetailSection>
      ) : null}

      {contact.requested_contact?.job?.campaign ? (
        <DetailSection title="Campaign">
          <EntitySummaryCard
            bordered={false}
            type="campaign"
            name={contact.requested_contact.job.campaign.name}
            href={`/campaigns/${contact.requested_contact.job.campaign.id}`}
            subtitle={
              campaignStatusLabel(
                contact.requested_contact.job.campaign.workflow_status
              ) || undefined
            }
          />
        </DetailSection>
      ) : null}

      <DetailSection title="Contact Methods">
        <InfoList>
          <EmailRow
            label="Work Email"
            email={contact.email_work}
            validity={contact.email_work_validity}
            onSave={(v) => saveField({ email_work: v || null })}
          />
          <EmailRow
            label="Personal Email"
            email={contact.email_personal}
            validity={contact.email_personal_validity}
            onSave={(v) => saveField({ email_personal: v || null })}
          />
          <EmailRow
            label="Other Email"
            email={contact.email_generic}
            validity={contact.email_generic_validity}
            onSave={(v) => saveField({ email_generic: v || null })}
          />
          <EditableInfoRow
            label="Phone"
            value={contact.phone_number}
            displayValue={displayPhone(contact.phone_number)}
            type="tel"
            placeholder="Add phone"
            // With an extension the "Copy phone + ext" action below is the
            // complete copy, so drop the built-in bare "Copy phone"; without one
            // the built-in is all that's needed.
            copyable={!phoneExt}
            // Numbers-only, formatted as a US phone as you type; must be a valid
            // number to save (stored canonically as E.164).
            format={formatPhoneDraft}
            validate={(v) =>
              isValidPhoneNumber(v, 'US') ? null : 'Enter a valid phone number'
            }
            actions={
              contact.phone_number
                ? [
                    {
                      label: 'Call',
                      icon: <PiPhone />,
                      onSelect: () => {
                        window.location.href = telHref(
                          contact.phone_number,
                          contact.extension
                        )
                      },
                    },
                    // Only offered when there's an extension — otherwise it's
                    // identical to the built-in "Copy phone" (`copyable`).
                    ...(phoneExt
                      ? [
                          {
                            label: 'Copy phone + ext',
                            icon: <PiCopy />,
                            onSelect: () => {
                              const shown =
                                displayPhone(contact.phone_number) ??
                                contact.phone_number
                              void navigator.clipboard?.writeText(
                                `${shown} ext. ${phoneExt}`
                              )
                            },
                          },
                        ]
                      : []),
                  ]
                : undefined
            }
            onCommit={(v) => saveField({ phone_number: v ? toE164(v) : '' })}
          />
          <EditableInfoRow
            label="Extension"
            value={contact.extension}
            placeholder="Add extension"
            onCommit={(v) => saveField({ extension: v })}
          />
          <EditableInfoRow
            label="LinkedIn"
            value={contact.linkedin_url}
            placeholder="Add LinkedIn URL"
            copyable
            actions={
              contact.linkedin_url
                ? [
                    {
                      label: 'Open LinkedIn',
                      icon: <PiLinkedinLogo />,
                      onSelect: () =>
                        window.open(
                          contact.linkedin_url,
                          '_blank',
                          'noreferrer'
                        ),
                    },
                  ]
                : undefined
            }
            onCommit={(v) => saveField({ linkedin_url: v })}
          />
          <EditableInfoRow
            label="Website"
            value={contact.url}
            placeholder="Add website"
            copyable
            actions={
              contact.url
                ? [
                    {
                      label: 'Open website',
                      icon: <PiGlobe />,
                      onSelect: () =>
                        window.open(contact.url, '_blank', 'noreferrer'),
                    },
                  ]
                : undefined
            }
            onCommit={(v) => saveField({ url: v ? normalizeWebsite(v) : '' })}
          />
        </InfoList>
      </DetailSection>

      {hasSequence ? (
        <DetailSection title="Engagement">
          <InfoList>
            <InfoRow label="Sequence">
              <Badge color="sky">{contact.seq_membership_status}</Badge>
            </InfoRow>
            {contact.seq_total_steps ? (
              <InfoRow label="Step" numeric>
                {contact.seq_steps_completed ?? 0} of {contact.seq_total_steps}
              </InfoRow>
            ) : null}
            <InfoRow label="Emails Sent" numeric>
              {contact.seq_emails_sent ?? 0}
            </InfoRow>
            <InfoRow label="Replies" numeric>
              {contact.seq_replies ?? 0}
            </InfoRow>
            {contact.date_last_sent ? (
              <InfoRow label="Last Sent" numeric>
                {fmtTimestamp(contact.date_last_sent)}
              </InfoRow>
            ) : null}
            {contact.date_last_received ? (
              <InfoRow label="Last Received" numeric>
                {fmtTimestamp(contact.date_last_received)}
              </InfoRow>
            ) : null}
          </InfoList>
        </DetailSection>
      ) : null}

      {deals.length > 0 ? (
        <DetailSection title="Deals">
          {/* ~4 rows before scroll, to keep the rail compact. */}
          <div className="fbui-scroll flex max-h-48 flex-col overflow-y-auto">
            {deals.map((deal) => (
              <EntityRow
                key={deal.id}
                type="deal"
                title={
                  deal.deal_display_name ||
                  deal.name ||
                  `Deal #${deal.identifier ?? deal.id}`
                }
                subtitle={`#${deal.identifier ?? deal.id}`}
                onClick={() => onOpenDeal(deal.id)}
              />
            ))}
          </div>
        </DetailSection>
      ) : null}

      <DetailSection
        title="Labels"
        revealActions
        headerRight={
          // A hover-revealed link (not a modal) to the contact-labels settings
          // tab, where labels are created/renamed/colored. `asChild` renders the
          // neutral icon-button as a real <a> (SPA Link) so cmd-click opens it
          // in a new tab. The in-body "Edit labels" pill still handles assigning.
          <Button
            asChild
            variant="neutral"
            iconOnly
            size="sm"
            aria-label="Manage labels in settings"
            title="Manage labels in settings"
          >
            <Link to="/settings/data/contact-tags">
              <PiGear />
            </Link>
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          {contact.tags?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {contact.tags.map((tag) => (
                <TagPill
                  key={tag.id}
                  label={tag.tag}
                  color={tag.color}
                  textColor={tag.textColor}
                />
              ))}
            </div>
          ) : null}
          <AddPill className="self-start" onClick={onAddLabel}>
            {contact.tags?.length ? 'Edit labels' : 'Add label'}
          </AddPill>
        </div>
      </DetailSection>
    </DetailPanel>
  )
}

/** A contact email row — inline-editable address with a validity badge. The
 *  address truncates and the badge stays pinned, so a long email won't wrap. */
function EmailRow({
  label,
  email,
  validity,
  onSave,
}: {
  label: string
  email?: string | null
  validity?: number | null
  onSave: (value: string) => void
}) {
  return (
    <EditableInfoRow
      label={label}
      value={email}
      type="email"
      placeholder={`Add ${label.toLowerCase()}`}
      copyable
      actions={
        email
          ? [
              {
                label: 'Send email',
                icon: <PiEnvelope />,
                onSelect: () => {
                  window.location.href = `mailto:${email}`
                },
              },
            ]
          : undefined
      }
      onCommit={onSave}
      adornment={
        email && validity != null ? (
          <EmailValidityChip validity={validity} />
        ) : undefined
      }
    />
  )
}

/** A compact validity indicator — a tinted icon chip (double-check for valid)
 *  with the full status on a hover tooltip, to save the row's horizontal space. */
function EmailValidityChip({ validity }: { validity: number }) {
  const label = EmailValidityLabels[validity as EmailValidity] ?? 'Unknown'
  const color = emailValidityColor(validity)
  const chipClass =
    color === 'success'
      ? 'bg-success-bg-subtle text-success-strong'
      : color === 'error'
        ? 'bg-error-bg-subtle text-error-strong'
        : color === 'amber'
          ? 'bg-warning-bg-subtle text-warning-strong'
          : 'bg-surface-sunken text-fg-tertiary'
  const icon =
    color === 'success' ? (
      <PiChecks size={13} />
    ) : color === 'error' ? (
      <PiWarningCircle size={13} />
    ) : (
      <PiWarning size={13} />
    )
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label={`Email validity: ${label}`}
            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${chipClass}`}
          >
            {icon}
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ─── Add / edit labels modal ──────────────────────────────────────────────────

/** fb-ui Dialog for managing a contact's labels — select existing labels or
 *  type a new one, then diff against the current set and add/remove via the
 *  contact-tags bulk endpoint. */
function AddLabelModal({
  open,
  onOpenChange,
  contactId,
  currentTags,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  contactId: number
  currentTags: Tag[]
  onSaved: () => void
}) {
  return (
    <OverlayWrapper isOpen={open} onOpenChange={onOpenChange}>
      <Backdrop className="max-w-md">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Labels</DialogTitle>
          </DialogHeader>
          {/* Keyed on `open` so the draft selection reseeds from the current
              labels every time the modal is opened (no effect needed). */}
          <LabelModalBody
            key={open ? 'open' : 'closed'}
            contactId={contactId}
            currentTags={currentTags}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}

function LabelModalBody({
  contactId,
  currentTags,
  onOpenChange,
  onSaved,
}: {
  contactId: number
  currentTags: Tag[]
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const api = useApiService()
  const { data: allTags } = api.useGetTags('contacts')

  const options: MultiComboboxOption[] = useMemo(
    () => (allTags ?? []).map((t) => ({ value: String(t.id), label: t.tag })),
    [allTags]
  )
  const knownIds = useMemo(
    () => new Set(options.map((o) => o.value)),
    [options]
  )

  const [selected, setSelected] = useState<string[]>(
    currentTags.map((t) => String(t.id))
  )
  const [busy, setBusy] = useState(false)

  const applyTagAction = (items: number[], action: 'add' | 'delete') =>
    api.commonManageTags({
      values: {
        items,
        action,
        include_ids: [contactId],
        exclude_ids: [],
        filter_params: {},
      },
      tableType: 'contacts-table',
      isContactTags: true,
    })

  const save = async () => {
    setBusy(true)
    try {
      const finalIds: number[] = []
      for (const val of selected) {
        if (knownIds.has(val)) {
          finalIds.push(Number(val))
          continue
        }
        const existing = (allTags ?? []).find(
          (t) => t.tag.toLowerCase() === val.toLowerCase()
        )
        if (existing) {
          finalIds.push(existing.id)
        } else {
          const created = await api.postTag({ tag: val }, 'contacts')
          if (created?.id) finalIds.push(created.id)
        }
      }

      const originalIds = currentTags.map((t) => t.id)
      const toAdd = finalIds.filter((id) => !originalIds.includes(id))
      const toRemove = originalIds.filter((id) => !finalIds.includes(id))

      if (toAdd.length) await applyTagAction(toAdd, 'add')
      if (toRemove.length) await applyTagAction(toRemove, 'delete')

      toast.success('Labels updated')
      onSaved()
      onOpenChange(false)
    } catch {
      toast.error('Failed to update labels')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <DialogBody>
        <MultiCombobox
          options={options}
          value={selected}
          onChange={setSelected}
          allowsCustomValue
          placeholder="Search or create a label…"
          aria-label="Contact labels"
          emptyMessage="No labels found"
        />
      </DialogBody>
      <DialogFooter>
        <DialogClose appearance="outline">Cancel</DialogClose>
        <Button onClick={save} disabled={busy}>
          Save labels
        </Button>
      </DialogFooter>
    </>
  )
}

/** Focused dialog for changing the contact's company (a searchable picker).
 *  Removing the company isn't offered — a contact must belong to one. */
function EditCompanyModal({
  open,
  onOpenChange,
  contactId,
  currentCompanyName,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  contactId: number
  currentCompanyName?: string
  onSaved: () => void
}) {
  return (
    <OverlayWrapper isOpen={open} onOpenChange={onOpenChange}>
      <Backdrop className="max-w-md">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Edit company</DialogTitle>
          </DialogHeader>
          {/* Keyed on `open` so the picker resets each time it opens. */}
          <EditCompanyBody
            key={open ? 'open' : 'closed'}
            contactId={contactId}
            currentCompanyName={currentCompanyName}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}

/** Body of the edit-company modal: a server-searched company picker wired to
 *  `updateContact` (assigns the picked company by id). */
function EditCompanyBody({
  contactId,
  currentCompanyName,
  onOpenChange,
  onSaved,
}: {
  contactId: number
  currentCompanyName?: string
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const api = useApiService()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(
    null
  )
  const [busy, setBusy] = useState(false)

  const { data: results, isFetching } = useQuery({
    queryKey: ['companySearch', search],
    queryFn: ({ signal }) => api.searchCompaniesList({ search }, signal),
    enabled: search.trim().length > 1,
    retry: false,
  })

  const options = useMemo(
    () =>
      (results ?? [])
        .filter((r) => r.contact_company_id != null)
        .map((r) => {
          // Custom (tenant-created) vs First Bite dataset.
          const source =
            r.is_unmatched != null
              ? r.is_unmatched
                ? 'Custom'
                : 'First Bite'
              : undefined
          // Chain (multi-location) vs a single door/location — with its address.
          const isChain = (r.location_count ?? 0) > 1
          const address = [r.address_line1, r.city, r.state]
            .filter(Boolean)
            .join(', ')
          const scope = isChain
            ? `Chain · ${(r.location_count as number).toLocaleString()} locations`
            : address
              ? `Location · ${address}`
              : 'Location'
          const meta = [source, r.category_name, scope]
            .filter(Boolean)
            .join(' · ')
          return { id: r.contact_company_id as number, name: r.name, meta }
        }),
    [results]
  )

  const emptyMessage =
    search.trim().length <= 1
      ? 'Type to search companies'
      : isFetching
        ? 'Searching…'
        : 'No companies found'

  const save = async () => {
    if (!selected) return
    setBusy(true)
    try {
      await api.updateContact(contactId, {
        contact_company: { id: selected.id },
      } as ContactCreateRequest)
      onSaved()
      onOpenChange(false)
      toast.success('Company updated')
    } catch {
      toast.error('Failed to update company')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <DialogBody>
        <div className="flex flex-col gap-3">
          {currentCompanyName ? (
            <p className="text-sm text-fg-secondary">
              Current company:{' '}
              <span className="font-medium text-fg">{currentCompanyName}</span>
            </p>
          ) : null}
          <Combobox
            aria-label="Company"
            menuTrigger="input"
            allowsEmptyCollection
            inputValue={search}
            onInputChange={setSearch}
            selectedKey={selected?.id ?? null}
            onSelectionChange={(key) => {
              const opt = options.find((o) => o.id === Number(key))
              if (opt) {
                setSelected({ id: opt.id, name: opt.name })
                // Reflect the picked company in the input (not the search term).
                setSearch(opt.name)
              }
            }}
          >
            <ComboboxInput placeholder="Search companies…" />
            <ComboboxContent emptyMessage={emptyMessage}>
              {options.map((o) => (
                <ComboboxItem key={o.id} id={o.id} textValue={o.name}>
                  <span className="flex flex-col">
                    <span>{o.name}</span>
                    {o.meta ? (
                      <span className="text-xs text-fg-secondary">
                        {o.meta}
                      </span>
                    ) : null}
                  </span>
                </ComboboxItem>
              ))}
            </ComboboxContent>
          </Combobox>
        </div>
      </DialogBody>
      <DialogFooter>
        <DialogClose appearance="outline">Cancel</DialogClose>
        <Button onClick={save} disabled={busy || !selected}>
          Save company
        </Button>
      </DialogFooter>
    </>
  )
}

// ─── Misc ─────────────────────────────────────────────────────────────────────

/** Expanded content for a real contact note — its rich-text body + attachment
 *  download links, with in-place Edit (rich-text) and Delete wired to the
 *  contact-notes endpoints. */
function ContactNoteView({
  contactId,
  note,
  onChanged,
}: {
  contactId: number
  note: INote
  onChanged: () => void
}) {
  const api = useApiService()
  const mentions = useNoteMentions()
  const { confirm, dialog: confirmDialog } = useConfirmDialog()
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const editorRef = useRef<HTMLDivElement | null>(null)
  const attachments = (note.attachments ?? []) as {
    id: number
    file: string
    original_filename: string
  }[]
  const att = useNoteEditAttachments(attachments)

  const seedRef = (el: HTMLDivElement | null) => {
    editorRef.current = el
    if (el && el.dataset.seeded !== '1') {
      el.innerHTML = note.content || ''
      el.dataset.seeded = '1'
    }
  }

  const cancelEdit = () => {
    setEditing(false)
    att.reset()
  }

  const saveEdit = async () => {
    setBusy(true)
    try {
      await api.editContactNote(
        contactId,
        note.id,
        editorRef.current?.innerHTML ?? '',
        att.toDelete,
        att.filesToAdd
      )
      setEditing(false)
      att.reset()
      onChanged()
      toast.success('Note updated')
    } catch {
      toast.error('Failed to update note')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!(await confirm({ title: 'Delete this note?' }))) return
    setBusy(true)
    try {
      await api.deleteContactNote(contactId, note.id)
      onChanged()
      toast.success('Note deleted')
    } catch {
      toast.error('Failed to delete note')
    } finally {
      setBusy(false)
    }
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <RichTextEditor
          contentLabel="Edit note"
          contentRef={seedRef}
          contentClassName="min-h-16"
          mentions={mentions}
          onCommand={(cmd) => {
            if (cmd === 'attachment') att.triggerFilePicker()
          }}
        />
        {att.node}
        <div className="flex justify-end gap-2">
          <Button appearance="outline" size="sm" onClick={cancelEdit}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy} onClick={saveEdit}>
            Save
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div
        className="text-sm text-fg [&_.mention]:font-medium [&_.mention]:text-brand-active [&_a]:text-brand-active [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-line [&_blockquote]:pl-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
        // Note body is first-party rich text authored by the team and stored
        // by our own backend (same as the deal-note card renders it).

        dangerouslySetInnerHTML={{ __html: note.content || '' }}
      />
      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {attachments.map((att) => (
            <a
              key={att.id}
              href={att.file}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-line-subtle bg-surface px-2 py-1 text-xs text-fg-secondary transition-colors hover:text-brand-active"
            >
              <PiPaperclip size={13} />
              <span className="max-w-[12rem] truncate">
                {att.original_filename}
              </span>
            </a>
          ))}
        </div>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          appearance="outline"
          size="sm"
          leftIcon={<PiPencilSimple aria-hidden="true" />}
          onClick={() => setEditing(true)}
        >
          Edit
        </Button>
        <DeleteIconButton
          label="Delete note"
          disabled={busy}
          onClick={remove}
        />
      </div>
      {confirmDialog}
    </div>
  )
}

function ActivityEmpty({
  label,
  icon,
}: {
  label: string
  /** Tab-specific glyph (pulse for activity, note for notes, envelope for
   *  emails) — sized 28 by the caller; colored via the wrapper. */
  icon: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <span className="text-fg-tertiary">{icon}</span>
      <p className="text-sm text-fg-secondary">{label}</p>
    </div>
  )
}
